import { useCallback, useEffect, useRef } from "react";

/**
 * Histórico de undo/redo do builder (ticket 01 — .scratch/builder-ux).
 *
 * Padrão "snapshot antes da ação": cada ação do usuário chama takeSnapshot()
 * ANTES de mutar o estado; undo/redo restauram esses snapshots via setNodes/setEdges.
 * Por isso um arraste inteiro vira 1 passo (snapshot em onNodeDragStart) e um
 * saveNode vira 1 passo (snapshot no início do saveNode).
 *
 * Decisões (grilling — ver ticket 01):
 *  - Em memória, sessão-only, limite de MAX_HISTORY passos, zerado ao carregar o fluxo.
 *  - Não sequestra o Ctrl+Z nativo dentro de campos de texto nem com o modal aberto.
 *  - Não aciona save no backend.
 */

const MAX_HISTORY = 50;

// Clones rasos o suficiente para blindar snapshots de mutação in-place
// (o saveNode reatribui node.data; a posição é trocada pelo reactflow).
function cloneNodes(nodes) {
	return nodes.map((n) => ({
		...n,
		data: { ...n.data },
		position: n.position ? { ...n.position } : n.position,
	}));
}

function cloneEdges(edges) {
	return edges.map((e) => ({ ...e }));
}

function isTextEntryTarget(el) {
	if (!el) return false;
	const tag = el.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
	if (el.isContentEditable) return true;
	return false;
}

export function useUndoRedo({ nodes, edges, setNodes, setEdges, isEditing }) {
	const history = useRef({ past: [], future: [] });

	// refs sempre com o estado corrente, para os event handlers não usarem closures velhas
	const nodesRef = useRef(nodes);
	const edgesRef = useRef(edges);
	const isEditingRef = useRef(isEditing);
	nodesRef.current = nodes;
	edgesRef.current = edges;
	isEditingRef.current = isEditing;

	const takeSnapshot = useCallback(() => {
		history.current.past.push({
			nodes: cloneNodes(nodesRef.current),
			edges: cloneEdges(edgesRef.current),
		});
		if (history.current.past.length > MAX_HISTORY) {
			history.current.past.shift();
		}
		// qualquer nova ação invalida o caminho de "avançar"
		history.current.future = [];
	}, []);

	const resetHistory = useCallback(() => {
		history.current = { past: [], future: [] };
	}, []);

	const undo = useCallback(() => {
		const { past, future } = history.current;
		if (!past.length) return;
		const previous = past.pop();
		future.push({
			nodes: cloneNodes(nodesRef.current),
			edges: cloneEdges(edgesRef.current),
		});
		setNodes(previous.nodes);
		setEdges(previous.edges);
	}, [setNodes, setEdges]);

	const redo = useCallback(() => {
		const { past, future } = history.current;
		if (!future.length) return;
		const next = future.pop();
		past.push({
			nodes: cloneNodes(nodesRef.current),
			edges: cloneEdges(edgesRef.current),
		});
		setNodes(next.nodes);
		setEdges(next.edges);
	}, [setNodes, setEdges]);

	useEffect(() => {
		const handler = (e) => {
			const mod = e.ctrlKey || e.metaKey;
			if (!mod) return;

			const key = (e.key || "").toLowerCase();
			if (key !== "z" && key !== "y") return;

			// Não sequestrar o undo/redo nativo enquanto o foco está num campo de
			// texto ou o modal de edição está aberto — o texto é dono do Ctrl+Z ali.
			if (isEditingRef.current || isTextEntryTarget(document.activeElement)) {
				return;
			}

			if (key === "z" && !e.shiftKey) {
				e.preventDefault();
				undo();
			} else if (key === "y" || (key === "z" && e.shiftKey)) {
				e.preventDefault();
				redo();
			}
		};

		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [undo, redo]);

	return { takeSnapshot, resetHistory, undo, redo };
}
